/**
 * client/src/lib/use-feature-flag.ts — React hook for evaluating feature flags.
 *
 * Fetches all flags via TanStack Query from the requireAuth-only evaluation
 * endpoint (GET /api/flags — NOT the operator-gated admin endpoint, which 403s
 * every non-platform-admin) and evaluates against the current environment.
 * Flags refresh every 60 s and are considered stale after 30 s.
 */

import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "./queryClient";
import { useCurrentUser } from "./use-current-user";
import type { FlagKey, FlagEvaluationContext } from "@shared/feature-flags";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** The trimmed evaluation record returned by GET /api/flags — 3 evaluation
 *  fields only (no id/description/timestamps). enabledOrgIdsJson is not on the
 *  wire: org-scoped flags are excluded server-side, fail closed. */
interface FlagEvalRecord {
  flagKey: string;
  isEnabled: boolean;
  enabledEnvironmentsJson: string[] | null;
  enabledOrgIdsJson?: string[] | null;
}

interface FlagsResponse {
  flags: FlagEvalRecord[];
}

function getCurrentEnvironment(): FlagEvaluationContext["environment"] {
  const mode = import.meta.env.MODE;
  if (mode === "production") return "production";
  if (mode === "staging") return "staging";
  return "development";
}

function evaluateFlagClient(
  flag: FlagEvalRecord | undefined,
  ctx: FlagEvaluationContext,
): boolean {
  if (!flag) return false;
  if (!flag.isEnabled) return false;

  const envs = flag.enabledEnvironmentsJson ?? [];
  if (envs.length > 0 && !envs.includes(ctx.environment)) return false;

  // Org-scoped flags are excluded server-side (fail closed) — this branch is a
  // guard in case the projection ever changes.
  const orgIds = flag.enabledOrgIdsJson ?? [];
  if (orgIds.length > 0) {
    if (!ctx.orgId || !orgIds.includes(ctx.orgId)) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Shared query + lookup — used by both hook variants below.
// ---------------------------------------------------------------------------

interface FlagLookup {
  /** Whether the flags query has resolved (data is defined). */
  loaded: boolean;
  /** The matched flag record, or undefined when absent from the loaded list. */
  flag: FlagEvalRecord | undefined;
  /** Evaluation context for the current environment. */
  ctx: FlagEvaluationContext;
}

/** The shared `["flags"]` query. `loaded` is true once it resolves. */
function useFlagsQuery(): { data: FlagsResponse | undefined; loaded: boolean } {
  // staleTime: Infinity — flag-eval only needs user identity once per session.
  const { user } = useCurrentUser({ staleTime: Infinity });

  const { data } = useQuery<FlagsResponse>({
    queryKey: ["flags"],
    queryFn: () => apiRequest<FlagsResponse>("/api/flags"),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  return { data, loaded: data !== undefined };
}

function useFlagLookup(flagKey: FlagKey, orgId?: string): FlagLookup {
  const { data, loaded } = useFlagsQuery();
  // Array.isArray guard (not just `data?.`): `data.flags` failing to be an
  // array — an unexpected response shape — must evaluate false rather than
  // throw on `.find`, so a malformed /api/flags response degrades every
  // consumer (nav items on EVERY authenticated page, onboarding step gates)
  // to "flag absent" instead of blanking the app (the dashboard blank-page
  // class — a hook iterating a non-array API payload).
  const flags = Array.isArray(data?.flags) ? data.flags : undefined;
  return {
    loaded,
    flag: flags?.find((f) => f.flagKey === flagKey),
    ctx: { environment: getCurrentEnvironment(), orgId },
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Whether the feature-flags query has resolved. Consumers that default a step
 *  ON while unloaded use this to reconcile once the real flag state arrives —
 *  see setup/index.tsx breadcrumb restore (the unloaded-default model can be a
 *  different shape than the loaded one). */
export function useFlagsLoaded(): boolean {
  return useFlagsQuery().loaded;
}

/** Return whether a feature flag is active for the current environment.
 *  Defaults OFF — an absent flag record (or an unloaded query) is `false`. */
export function useFeatureFlag(flagKey: FlagKey, orgId?: string): boolean {
  const { flag, ctx } = useFlagLookup(flagKey, orgId);
  return evaluateFlagClient(flag, ctx);
}

/**
 * Default-ON / opt-OUT flag variant. Returns `defaultWhenUnset` while the flags
 * query is still loading OR when the flag record is absent (no DB row), so a
 * step gated on this stays visible by default. A PRESENT record is honored via
 * evaluateFlagClient — an explicit `isEnabled=false` row HIDES the step.
 */
export function useFeatureFlagWithDefault(
  flagKey: FlagKey,
  defaultWhenUnset: boolean,
  orgId?: string,
): boolean {
  const { loaded, flag, ctx } = useFlagLookup(flagKey, orgId);
  if (!loaded) return defaultWhenUnset;
  if (!flag) return defaultWhenUnset;
  return evaluateFlagClient(flag, ctx);
}
